import { useEffect, useRef, useState } from 'react';
import { resolveValue, Toaster as ReactToaster, ToastBar, useToasterStore } from 'react-hot-toast';

const Toaster = () => {
  const { toasts } = useToasterStore();
  const [announcement, setAnnouncement] = useState('');
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const announcedIdRef = useRef<string | null>(null);

  // Mirror every toast message into a persistent aria-live region: react-hot-toast mounts
  // each toast's own live region at the same instant its content appears, which most screen
  // readers miss. This one already exists in the DOM, so only its text changes.
  useEffect(() => {
    const latest = toasts[0];
    if (!latest || latest.type === 'loading' || announcedIdRef.current === latest.id) return;

    announcedIdRef.current = latest.id;
    const text = resolveValue(latest.message, latest);
    if (typeof text === 'string') {
      setAnnouncement(text);
      // Claim focus so a later DOM removal elsewhere (e.g. a form unmounting) can't
      // yank focus back to <body> and interrupt the announcement in progress.
      liveRegionRef.current?.focus({ preventScroll: true });
    }
  }, [toasts]);

  return (
    <>
      <div
        ref={liveRegionRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        tabIndex={-1}
        data-ffid="toast-live-region"
        className="sr-only"
      >
        {announcement}
      </div>
      <ReactToaster
        toastOptions={{
          success: {
            iconTheme: {
              primary: '#67AC1E',
              secondary: '#FFFFFF',
            },
          },
          error: {
            iconTheme: {
              primary: '#DB0000',
              secondary: '#FFFFFF',
            },
          },
          style: {
            color: '#333333',
          },
          duration: 5000,
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div data-ffid={`toast-${t.type}`} className="flex">
                {icon}
                {message}
              </div>
            )}
          </ToastBar>
        )}
      </ReactToaster>
    </>
  );
};

export default Toaster;
